// touch: 2026-05-04-pause-inline-when-lightbox-opens
// Lazy-loaded PhotoSwipe lightbox.
//
// Why: eagerly loading the PhotoSwipe stylesheet from cdn.jsdelivr.net was
// adding ~300–700 ms to LCP (cross-origin render-blocking CSS) for every PDP
// visitor — even though most never zoom into images. This loader does zero
// network work on init; it just attaches a click delegate. On the first tap
// of a .tc-gal__zoom-link (or [data-zoom-btn]) it injects the PhotoSwipe
// CSS, dynamically imports the lightbox module, builds a dataSource from
// the gallery anchors, and opens at the clicked index. Subsequent taps
// reuse the loaded instance.
(function () {
  'use strict';

  let lbPromise = null;

  function loadCSS(href) {
    return new Promise((resolve, reject) => {
      if (document.querySelector('link[data-pswp-css]')) return resolve();
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.pswpCss = '1';
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  // Build a PhotoSwipe dataSource from the gallery anchors.
  function buildItems() {
    const anchors = Array.from(document.querySelectorAll('a.tc-gal__zoom-link'));
    return anchors.map((a) => {
      const isVideo = a.dataset.pswpType === 'video';
      const item = {
        src: a.href,
        width: parseInt(a.dataset.pswpWidth, 10) || 0,
        height: parseInt(a.dataset.pswpHeight, 10) || 0,
        element: a,
      };
      if (isVideo) {
        item.type = 'video';
        item.videoSrc = a.dataset.videoSrc;
      }
      return item;
    });
  }

  async function ensureLightbox() {
    if (lbPromise) return lbPromise;
    lbPromise = (async () => {
      // Load CSS + lightbox module in parallel for the first interaction.
      const [, mod] = await Promise.all([
        loadCSS('https://cdn.jsdelivr.net/npm/photoswipe@5.4.4/dist/photoswipe.css'),
        import('https://cdn.jsdelivr.net/npm/photoswipe@5.4.4/dist/photoswipe-lightbox.esm.min.js'),
      ]);
      const PhotoSwipeLightbox = mod.default;

      const lb = new PhotoSwipeLightbox({
        // Manual mode (no `gallery`/`children`) — click handling lives in
        // this loader so we never have a race between PhotoSwipe's click
        // delegate and ours during the first interaction.
        pswpModule: () => import('https://cdn.jsdelivr.net/npm/photoswipe@5.4.4/dist/photoswipe.esm.min.js'),
        bgOpacity: 1,
        showHideAnimationType: 'fade',
        wheelToZoom: true,
        clickToCloseNonZoomable: true,
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,
        maxZoomLevel: 4,
      });

      lb.addFilter('itemData', (itemData) => {
        const el = itemData.element;
        if (el && el.dataset && el.dataset.pswpType === 'video') {
          itemData.type = 'video';
          itemData.videoSrc = el.dataset.videoSrc;
        }
        return itemData;
      });

      lb.on('contentLoad', (e) => {
        const { content } = e;
        if (content.type !== 'video') return;
        e.preventDefault();
        const wrap = document.createElement('div');
        wrap.className = 'pswp__video-wrap';
        const video = document.createElement('video');
        video.className = 'pswp__video';
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.src = content.data.videoSrc;
        wrap.appendChild(video);
        content.element = wrap;
      });

      lb.on('contentDeactivate', (e) => {
        const { content } = e;
        if (content.type !== 'video' || !content.element) return;
        const v = content.element.querySelector('video');
        if (v && !v.paused) v.pause();
      });

      // ---- Coexist with inline gallery autoplay ----
      // When the lightbox opens, pause any inline gallery video so we
      // don't have two playheads running at once (and so audio in the
      // lightbox isn't fighting muted background loops). On close,
      // restart the active inline video if it was playing.
      let _resumeInlineOnClose = null;
      lb.on('beforeOpen', () => {
        const playing = document.querySelector('.tc-gal__video[data-playing="true"]');
        if (playing && !playing.paused) {
          playing.pause();
          _resumeInlineOnClose = playing;
        } else {
          _resumeInlineOnClose = null;
        }
      });
      lb.on('close', () => {
        const v = _resumeInlineOnClose;
        _resumeInlineOnClose = null;
        if (!v) return;
        // Only resume if this slide is still the active one (user may
        // have swiped to a different slide via the lightbox).
        const item = v.closest('[data-media-item]');
        const active = item && item.classList.contains('is-active');
        if (active) {
          const p = v.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else {
          delete v.dataset.playing;
        }
      });

      lb.init();
      window.__tcPswpLightbox = lb;
      return lb;
    })();
    return lbPromise;
  }

  // Click delegation: capture phase so we run before any other handler and
  // can preventDefault before the browser navigates to the anchor href.
  document.addEventListener(
    'click',
    async (e) => {
      const anchor = e.target.closest('a.tc-gal__zoom-link');
      const zoomBtn = e.target.closest('[data-zoom-btn]');
      if (!anchor && !zoomBtn) return;

      // Don't preventDefault if user opened in new tab (modifier keys / middle click)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;

      e.preventDefault();

      try {
        const lb = await ensureLightbox();

        // Resolve which index to open at.
        const anchors = Array.from(document.querySelectorAll('a.tc-gal__zoom-link'));
        let index = 0;
        if (anchor) {
          index = Math.max(0, anchors.indexOf(anchor));
        } else if (zoomBtn) {
          const active = document.querySelector('.tc-gal__item.is-active');
          if (active && active.dataset.index) {
            index = parseInt(active.dataset.index, 10) || 0;
          }
        }
        lb.loadAndOpen(index, buildItems());
      } catch (err) {
        console.error('[tc-pswp] lightbox load failed', err);
      }
    },
    true /* capture */
  );
})();
